
<template>
    <button
      type="button"
      class="structure-worker"
      :class="[
        `worker-level-${level}`,
        {
          'worker-dimmed': dimmed
        }
      ]"
      :disabled="!employee?.userId"
      :title="employee?.userId ? 'Открыть профиль' : undefined"
      @click="openProfile"
    >
  
      <!-- =====================================================
           ACCENT
      ====================================================== -->
  
      <span class="worker-accent"></span>
  
      <!-- =====================================================
           PORTRAIT
      ====================================================== -->
  
      <div class="worker-portrait">
  
        <img
          v-if="employee?.imageURL && !imageFailed"
          :src="employee.imageURL"
          :alt="employee.name || 'Сотрудник'"
          loading="lazy"
          @error="imageFailed = true"
        />
  
        <span v-else class="worker-initials">
          {{ initials }}
        </span>
  
        <span class="worker-revealed-badge">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12L10 17L19 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
  
      </div>
  
      <!-- =====================================================
           ROLE
      ====================================================== -->
  
      <span class="worker-role">
        {{ roleLabel }}
      </span>
  
      <!-- =====================================================
           NAME
      ====================================================== -->
  
      <strong class="worker-name">
        {{ employee?.name || "Пользователь" }}
      </strong>
  
      <!-- =====================================================
           SPECIALTY
      ====================================================== -->
  
      <span class="worker-specialty">
        {{
          employee?.specifics &&
          employee.specifics !== "Не указано"
            ? employee.specifics
            : "Специальность не указана"
        }}
      </span>
  
      <!-- =====================================================
           STATUS
      ====================================================== -->
  
      <div class="worker-bottom">
  
        <span class="worker-status-dot" :class="statusClass"></span>
  
        <span class="worker-status-text">
          {{ statusText }}
        </span>
  
      </div>
  
      <!-- =====================================================
           OPEN INDICATOR
      ====================================================== -->
  
      <span class="worker-open-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12H19M19 12L13 6M19 12L13 18"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
  
    </button>
  </template>
  
  <script setup>
  import {
    computed,
    ref,
    watch
  } from "vue";
  
  // ============================================================
  // PROPS
  // ============================================================
  
  const props = defineProps({
  
    employee: {
      type: Object,
      required: true
    },
  
    level: {
      type: Number,
      default: 3,
      validator: value => [2, 3].includes(value)
    },
  
    dimmed: {
      type: Boolean,
      default: false
    }
  
  });
  
  // ============================================================
  // EMITS
  // ============================================================
  
  const emit = defineEmits(["open"]);
  
  // ============================================================
  // IMAGE STATE
  // ============================================================
  
  const imageFailed = ref(false);
  
  watch(
    () => props.employee?.imageURL,
    () => {
      imageFailed.value = false;
    }
  );
  
  // ============================================================
  // INITIALS
  // ============================================================
  
  const initials = computed(() => {
    const parts = String(
      props.employee?.name || ""
    )
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  
    if (!parts.length) {
      return "HR";
    }
  
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  
    return (
      parts[0].charAt(0) +
      parts[1].charAt(0)
    ).toUpperCase();
  });
  
  // ============================================================
  // ROLE
  // ============================================================
  
  const roleLabel = computed(() => {
    return props.level === 2
      ? "РУКОВОДИТЕЛЬ"
      : "СПЕЦИАЛИСТ";
  });
  
  // ============================================================
  // STATUS
  // ============================================================
  
  const statusText = computed(() => {
    const value = props.employee?.isCurrentEmployee;
  
    if (value === true) {
      return "Работает";
    }
  
    if (value === false) {
      return "Работал ранее";
    }
  
    return "Статус неизвестен";
  });
  
  const statusClass = computed(() => {
    const value = props.employee?.isCurrentEmployee;
  
    if (value === true) {
      return "status-current";
    }
  
    if (value === false) {
      return "status-former";
    }
  
    return "status-unknown";
  });
  
  // ============================================================
  // OPEN PROFILE
  // ============================================================
  
  function openProfile() {
    if (!props.employee?.userId) {
      return;
    }
  
    emit("open", props.employee);
  }
  </script>
  
  <style scoped>
  
  /* ============================================================
     BASE
  ============================================================ */
  
  .structure-worker {
    --worker-primary: #347bdc;
    --worker-background: #e1edff;
    --worker-background-light: #f2f7ff;
  
    position: relative;
  
    min-width: 0;
    min-height: 188px;
  
    display: flex;
    flex-direction: column;
    align-items: center;
  
    padding: 18px 7px 12px;
  
    overflow: hidden;
  
    color: var(--text, #243247);
    background: var(--surface, #ffffff);
  
    border:
      1px solid
      var(--border, rgba(40, 78, 134, 0.095));
  
    border-radius: 17px;
  
    box-shadow:
      var(--shadow, 0 10px 28px rgba(29, 64, 113, 0.05));
  
    font-family: "Eastman-Regular", sans-serif;
    text-align: center;
  
    cursor: pointer;
  
    transition:
      transform 0.22s ease,
      border-color 0.22s ease,
      box-shadow 0.22s ease,
      opacity 0.22s ease;
  }
  
  .structure-worker:hover:not(:disabled) {
    transform: translateY(-5px);
  
    border-color: rgba(48, 128, 237, 0.37);
  
    box-shadow:
      0 16px 35px rgba(43, 95, 175, 0.13);
  }
  
  .structure-worker:focus-visible {
    outline: 2px solid var(--worker-primary);
    outline-offset: 3px;
  }
  
  .structure-worker:disabled {
    cursor: default;
  }
  
  /* ============================================================
     LEVELS
  ============================================================ */
  
  .worker-level-2 {
    --worker-primary: #347bdc;
    --worker-background: #e1edff;
    --worker-background-light: #f2f7ff;
  }
  
  .worker-level-3 {
    --worker-primary: #3e9c88;
    --worker-background: #dff3ec;
    --worker-background-light: #f0faf6;
  }
  
  /* ============================================================
     ACCENT
  ============================================================ */
  
  .worker-accent {
    position: absolute;
  
    top: 0;
  
    width: 39px;
    height: 3px;
  
    border-radius: 0 0 4px 4px;
  
    background: var(--worker-primary);
  }
  
  /* ============================================================
     PORTRAIT
  ============================================================ */
  
  .worker-portrait {
    position: relative;
  
    width: 72px;
    height: 72px;
  
    flex-shrink: 0;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    margin-bottom: 13px;
  
    border-radius: 21px;
  
    color: var(--worker-primary);
  
    background:
      linear-gradient(
        145deg,
        var(--worker-background),
        var(--worker-background-light)
      );
  }
  
  .worker-portrait img {
    width: 100%;
    height: 100%;
  
    display: block;
  
    object-fit: cover;
    border-radius: inherit;
  }
  
  .worker-initials {
    font-family: "Eastman-Bold", sans-serif;
    font-size: 22px;
  }
  
  /* ============================================================
     REVEALED BADGE
  ============================================================ */
  
  .worker-revealed-badge {
    position: absolute;
  
    right: -7px;
    bottom: -7px;
  
    width: 25px;
    height: 25px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #ffffff;
    background: var(--worker-primary);
  
    border:
      3px solid
      var(--surface, #ffffff);
  
    border-radius: 50%;
  
    box-shadow:
      0 4px 12px rgba(44, 110, 197, 0.12);
  }
  
  .worker-revealed-badge svg {
    width: 14px;
    height: 14px;
  }
  
  /* ============================================================
     ROLE
  ============================================================ */
  
  .worker-role {
    width: 100%;
  
    color: var(--worker-primary);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 8px;
  
    letter-spacing: 0.45px;
  }
  
  /* ============================================================
     NAME
  ============================================================ */
  
  .worker-name {
    width: 100%;
  
    margin-top: 8px;
  
    color: var(--text, #243247);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 10px;
  
    line-height: 1.35;
  
    overflow-wrap: anywhere;
  }
  
  /* ============================================================
     SPECIALTY
  ============================================================ */
  
  .worker-specialty {
    width: 100%;
  
    margin-top: 6px;
  
    color: var(--muted, #8896aa);
  
    font-size: 9px;
    line-height: 1.4;
  
    overflow-wrap: anywhere;
  }
  
  /* ============================================================
     STATUS
  ============================================================ */
  
  .worker-bottom {
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 6px;
  
    margin-top: auto;
    padding-top: 11px;
  
    max-width: 100%;
  }
  
  .worker-status-dot {
    width: 6px;
    height: 6px;
  
    flex-shrink: 0;
  
    border-radius: 50%;
  }
  
  .status-current {
    background: #3eaf7a;
  }
  
  .status-former {
    background: #c3a068;
  }
  
  .status-unknown {
    background: #a4b3c4;
  }
  
  .worker-status-text {
    color: var(--muted, #8896aa);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 8px;
  
    line-height: 1.25;
  }
  
  /* ============================================================
     OPEN INDICATOR
  ============================================================ */
  
  .worker-open-icon {
    position: absolute;
  
    top: 10px;
    right: 10px;
  
    width: 23px;
    height: 23px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    border-radius: 7px;
  
    background: rgba(52, 123, 220, 0.08);
    color: var(--worker-primary);
  
    opacity: 0;
  
    transform: translateX(-4px);
  
    transition:
      opacity 0.18s ease,
      transform 0.18s ease;
  }
  
  .worker-open-icon svg {
    width: 14px;
    height: 14px;
  }
  
  .structure-worker:hover .worker-open-icon,
  .structure-worker:focus-visible .worker-open-icon {
    opacity: 1;
    transform: translateX(0);
  }
  
  /* ============================================================
     FILTER DIMMING
  ============================================================ */
  
  .worker-dimmed {
    opacity: 0.25;
  
    filter: grayscale(0.75);
  }
  
  /* ============================================================
     DARK THEME
  ============================================================ */
  
  :global(html[data-theme="dark"]) .worker-level-2,
  :global(.structure-page.dark) .worker-level-2 {
    --worker-background: #2f4057;
    --worker-background-light: #273347;
    --worker-primary: #89b6f4;
  }
  
  :global(html[data-theme="dark"]) .worker-level-3,
  :global(.structure-page.dark) .worker-level-3 {
    --worker-background: #30473e;
    --worker-background-light: #283a33;
    --worker-primary: #8bcbb6;
  }
  
  @media (prefers-reduced-motion: reduce) {
    .structure-worker,
    .worker-open-icon {
      transition: none;
    }
  }
  </style>
  